import { useState, useEffect } from "react";
import { CanalTV } from "@/entities/CanalTV";
import { Tv, Search, Play, Radio, ChevronDown } from "lucide-react";

const CATEGORIAS = ["todas", "noticias", "entretenimiento", "deportes", "musica", "peliculas", "infantil", "otro"];

function getEmbedUrl(canal) {
  if (!canal?.url_stream) return null;
  if (canal.tipo_stream === "youtube") {
    const match = canal.url_stream.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
    if (canal.url_stream.includes("youtube.com/embed")) return canal.url_stream + "?autoplay=1";
    return canal.url_stream;
  }
  return canal.url_stream;
}

export default function TvEnVivo() {
  const [canales, setCanales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canalActivo, setCanalActivo] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("todas");

  useEffect(() => {
    async function cargar() {
      const data = await CanalTV.filter({ activo: true }, "orden", 100);
      setCanales(data);
      if (data.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const canalId = urlParams.get("canal");
        const found = canalId ? data.find(c => c.id === canalId) : null;
        setCanalActivo(found || data[0]);
      }
      setLoading(false);
    }
    cargar();
  }, []);

  const canalesFiltrados = canales.filter(c => {
    const matchBusqueda = c.nombre?.toLowerCase().includes(busqueda.toLowerCase());
    const matchCategoria = categoria === "todas" || c.categoria === categoria;
    return matchBusqueda && matchCategoria;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-yellow-500/20 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Tv className="w-4 h-4 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white">TV en Vivo</h1>
          <span className="flex items-center gap-1 bg-red-500/20 text-red-400 text-xs font-bold px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : canales.length === 0 ? (
          <div className="text-center py-20 text-gray-600">
            <Tv className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">No hay canales disponibles</p>
            <p className="text-sm mt-1">Agrega canales desde el panel de administración</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Player */}
            <div className="flex-1">
              {canalActivo && (
                <div>
                  <div className="relative w-full bg-gray-950 rounded-2xl overflow-hidden border border-yellow-500/20"
                    style={{ paddingTop: "56.25%" }}>
                    {canalActivo.tipo_stream === "m3u8" ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950 text-gray-400">
                        <Radio className="w-12 h-12 mb-3 text-yellow-400 animate-pulse" />
                        <p className="font-semibold text-white">{canalActivo.nombre}</p>
                        <p className="text-sm mt-1">Stream HLS — usa un reproductor compatible</p>
                        <a href={canalActivo.url_stream} target="_blank" rel="noreferrer"
                          className="mt-3 bg-yellow-400 text-black font-bold px-4 py-2 rounded-full text-sm hover:bg-yellow-300 transition-colors">
                          Abrir Stream
                        </a>
                      </div>
                    ) : (
                      <iframe
                        key={canalActivo.id}
                        src={getEmbedUrl(canalActivo)}
                        className="absolute inset-0 w-full h-full"
                        allowFullScreen
                        allow="autoplay; encrypted-media; picture-in-picture"
                        title={canalActivo.nombre}
                      />
                    )}
                  </div>
                  <div className="mt-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <h2 className="text-lg font-bold text-white">{canalActivo.nombre}</h2>
                      <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full capitalize">
                        {canalActivo.categoria}
                      </span>
                    </div>
                    {canalActivo.descripcion && (
                      <p className="text-gray-500 text-sm mt-1">{canalActivo.descripcion}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Lista de canales */}
            <div className="lg:w-80 xl:w-96 flex flex-col gap-3">
              {/* Búsqueda */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Buscar canal..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500/50"
                />
              </div>
              {/* Filtro categoría */}
              <div className="relative">
                <select
                  value={categoria}
                  onChange={e => setCategoria(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white appearance-none focus:outline-none focus:border-yellow-500/50 capitalize"
                >
                  {CATEGORIAS.map(c => (
                    <option key={c} value={c} className="capitalize">{c === "todas" ? "Todas las categorías" : c}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>

              {/* Lista */}
              <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-800">
                {canalesFiltrados.length === 0 ? (
                  <p className="text-center text-gray-600 py-6 text-sm">Sin resultados</p>
                ) : canalesFiltrados.map(canal => (
                  <button
                    key={canal.id}
                    onClick={() => setCanalActivo(canal)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left w-full ${
                      canalActivo?.id === canal.id
                        ? "border-yellow-500/60 bg-yellow-500/10"
                        : "border-gray-800 bg-gray-900 hover:border-gray-700 hover:bg-gray-800"
                    }`}
                  >
                    <div className="w-14 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center">
                      {canal.imagen_portada ? (
                        <img src={canal.imagen_portada} alt={canal.nombre} className="w-full h-full object-cover" />
                      ) : (
                        <Tv className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm truncate ${canalActivo?.id === canal.id ? "text-yellow-400" : "text-white"}`}>
                        {canal.nombre}
                      </p>
                      <p className="text-gray-600 text-xs capitalize">{canal.categoria}</p>
                    </div>
                    {canalActivo?.id === canal.id && (
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}